import { memo } from "react";
import { LineLoading } from "react-migration/domains/dashboard/documents/components/LineLoading";
import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import { ProjectDropdownList } from "./ProjectDropdownList";
import clsx from "clsx";
import { ProjectQuery } from "@gql/sites-api/graphql";

interface ProjectDropdownMobileProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedProject?: ProjectQuery["project"] | null;
  loading?: boolean;
  onCreateNewProject: () => void;
  onSelectProject: (projectId: string) => void;
}

export const ProjectDropdownMobile = ({
  isOpen,
  setIsOpen,
  selectedProject,
  loading,
  onCreateNewProject,
  onSelectProject,
}: ProjectDropdownMobileProps) => {
  return (
    <div
      data-testid="project-dropdown-mobile"
      className={clsx("w-full bg-background-surface", isOpen && "pt-4 top-0! absolute! z-99!")}
    >
      <div data-testid="project-dropdown-trigger" className="h-12 w-full px-3">
        <div className="flex flex-1 items-center justify-between relative w-full h-full overflow-hidden cursor-pointer">
          <div
            className={clsx(
              "h-full grow flex items-center justify-start gap-x-4 overflow-hidden w-full",
              "*:first:max-w-full",
              isOpen ? "*:last:shrink *:last:justify-start *:last:basis-auto *:last:overflow-hidden" : "*:first:w-full"
            )}
          >
            {isOpen && (
              <Button
                variant="tertiary"
                size="lg"
                className="ring-0! shadow-none! h-9 w-9"
                onClick={() => setIsOpen(false)}
              >
                <Icon size="md" cursor="pointer" icon="icon-lt-arrow-left-s-line" />
              </Button>
            )}
            {loading && !selectedProject ? (
              <>
                <LineLoading />
              </>
            ) : (
              selectedProject && (
                <Button
                  variant="clear"
                  size="lg"
                  className={clsx(
                    "w-full text-left ring-0! shadow-none! text-text-base-primary text-base font-medium flex items-center justify-start",
                    isOpen ? "justify-start" : "justify-between!"
                  )}
                  onClick={() => setIsOpen(true)}
                  tailIcon={!isOpen ? "lt-arrow-right-s-line" : undefined}
                >
                  <span className="truncate">{selectedProject.title}</span>
                </Button>
              )
            )}
          </div>
        </div>
      </div>
      <div
        data-testid="project-dropdown-content-mobile"
        className={clsx(
          "w-0 right-0 bg-background-surface h-0 overflow-hidden transition-[width]",
          isOpen && "h-[calc(100dvh-60px)] w-dvw"
        )}
      >
        {isOpen && (
          <ProjectDropdownContentMobile onCreateNewProject={onCreateNewProject} onSelectProject={onSelectProject} />
        )}
      </div>
    </div>
  );
};

interface ProjectDropdownContentMobileProps {
  onCreateNewProject(): void;
  onSelectProject(projectId: string): void;
}

const ProjectDropdownContentMobile = memo(
  ({ onCreateNewProject, onSelectProject }: ProjectDropdownContentMobileProps) => {
    return (
      <div className={"p-4 pb-9 flex flex-col gap-y-6 w-full h-[calc(100%-var(--navbar-mobile-height))] select-none"}>
        <ProjectDropdownList onSelectProject={onSelectProject} />

        <div className="flex flex-row justify-end shrink-0 min-h-9 mt-auto w-full">
          <Button variant="primary" size="sm" className="w-auto" onClick={onCreateNewProject}>
            <Icon size="lg" cursor="pointer" icon="icon-lt-add-line" />
            New workspace
          </Button>
        </div>
      </div>
    );
  }
);

ProjectDropdownContentMobile.displayName = "ProjectDropdownContentMobile";
